<template>
  <div
    class="achievements-content"
    :class="{ locked: selectedAchievement && !selectedAchievement.available }"
  >
    <div class="achievement-display">
      <div style="flex-grow: 1"></div>
      <div class="achievement-display-content">
        <div v-if="showNotification" class="desktop-only notification-banner pulse-animation">
          Select an achievement to see a preview! 🎮
          <button class="close-btn" @click="closeNotification">✕</button>
        </div>
        <div class="achievements-list-container">
          <!-- Left Chevron -->
          <button
            v-if="currentIndex > 0"
            class="chevron-btn chevron-left"
            @click="navigateToAchievement(currentIndex - 1)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div
            ref="achievementsList"
            class="achievements-list"
            @scroll="handleScroll"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @touchcancel="handleTouchEnd"
            @wheel.prevent
          >
            <achievement-display
              ref="achievementDisplay"
              v-for="achievement in achievements"
              :key="achievement.id"
              :achievement="achievement"
              :position="achievements.indexOf(achievement) + 1"
              :numMoreStars="achievements.filter((a) => a.stars > achievement.stars).length"
              :numLessStars="achievements.filter((a) => a.stars < achievement.stars).length"
              :numSameStars="achievements.filter((a) => a.stars === achievement.stars).length"
              :boardId="boardId"
              :plays="plays"
              :score="score"
              :rank="rank"
              :coordsAndRarities="coordsAndRarities"
              :hasMadeGuessInInfiniteMode="hasMadeGuessInInfiniteMode"
              :class="{ active: selectedAchievement.id === achievement.id }"
            />

            <!-- No More Achievements Panel -->
            <div class="no-more-achievements-panel">
              <div class="no-more-content">
                <div class="no-more-title">Explore More</div>
                <div class="no-more-icon"><RisingArrowIcon width="100%" height="100%" /></div>
                <div class="no-more-description">
                  Want to dive deep into your data or see the answers from this game?
                </div>
                <div class="no-more-mini-description">
                  Click <span class="desktop-only">Score Distribution</span
                  ><ChartIcon width="16" height="16" class="inline-svg mobile-only" /> and
                  <span class="desktop-only">Game Summary</span
                  ><GridIcon width="16" height="16" class="inline-svg mobile-only" /> in the header
                  to access this data at any time.
                </div>
                <div class="no-more-mini-description no-more-last">
                  Keep scrolling to see locked achievements.
                </div>
                <div class="no-more-buttons">
                  <button class="button" @click="$emit('select-score-distribution')">
                    <ChartIcon width="24" height="24" />
                    <span>Score Distribution</span>
                  </button>
                  <button class="button" @click="$emit('select-game-summary')">
                    <GridIcon width="24" height="24" />
                    <span>Game Summary</span>
                  </button>
                </div>
              </div>
            </div>

            <achievement-display
              ref="achievementDisplay"
              v-for="achievement in lockedAchievements"
              :key="achievement.id"
              :achievement="achievement"
              :position="achievements.indexOf(achievement) + 1"
              :numMoreStars="achievements.filter((a) => a.stars > achievement.stars).length"
              :numLessStars="achievements.filter((a) => a.stars < achievement.stars).length"
              :numSameStars="achievements.filter((a) => a.stars === achievement.stars).length"
              :boardId="boardId"
              :plays="plays"
              :score="score"
              :rank="rank"
              :coordsAndRarities="coordsAndRarities"
              :hasMadeGuessInInfiniteMode="hasMadeGuessInInfiniteMode"
              :class="{ active: selectedAchievement.id === achievement.id }"
            />
          </div>

          <!-- Right Chevron -->
          <button
            v-if="currentIndex < allAchievementsForScroll.length - 1"
            class="chevron-btn chevron-right"
            @click="navigateToAchievement(currentIndex + 1)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      <div style="flex-grow: 1"></div>
      <!-- <div class="mobile-only game-summary-notification" v-if="!hasChangedScoreModalTab">
        Click
        <span style="color: #e6c07b"
          ><GridIcon width="15" height="15" style="vertical-align: middle"
        /></span>
        at the top for Game Summary.
      </div> -->
    </div>

    <!-- Desktop view: Achievement List Section -->
    <achievement-list-section
      class="desktop-only"
      :todays-achievements="achievements"
      :locked-achievements="lockedAchievements"
      :selected-achievement="selectedAchievement"
      @achievement-selected="selectAchievement"
    />
    <div class="desktop-only game-summary-notification" v-if="!hasChangedScoreModalTab">
      Game Summary has moved. Click the
      <a
        href="#"
        @click.prevent="$emit('select-game-summary')"
        style="color: #e6c07b; cursor: pointer"
        >menu</a
      >
      at top to find it.
    </div>

    <!-- Mobile view: Achievements Carousel -->
    <achievements-carousel-dots
      v-if="allAchievementsForScroll && allAchievementsForScroll.length > 0"
      class="mobile-only"
      :all-achievements="allAchievementsForScroll"
      :selected-achievement="selectedAchievement"
      @achievement-selected="selectAchievement"
    />
  </div>
</template>

<script>
  import AchievementListSection from './achievementListSection.vue';
  import AchievementsCarouselDots from './achievementsCarouselDots.vue';
  import AchievementDisplay from './achivementDisplay.vue';
  import { ChartIcon, GridIcon, RisingArrowIcon } from '@/components/svgs';

  export default {
    name: 'AchievementsTab',
    components: {
      AchievementListSection,
      AchievementsCarouselDots,
      AchievementDisplay,
      ChartIcon,
      GridIcon,
      RisingArrowIcon,
    },
    props: [
      'boardId',
      'board',
      'archiveMode',
      'score',
      'rank',
      'plays',
      'statsData',
      'coordsAndRarities',
      'rarityPercentages',
      'hasMadeGuessInInfiniteMode',
      'hasChangedScoreModalTab',
    ],
    data() {
      return {
        showNotification: !localStorage.getItem('achievementNotificationDismissed'),
        selectedAchievement: null,
        achievements: [],
        lockedAchievements: [],
        scrollTimeout: null,
        allAchievementsForScroll: [],
        touchStartX: 0,
        touchEndX: 0,
        touchCurrentX: 0,
        touchCurrentScrollDir: 0,
        isScrolling: false,
        currentIndex: 0,
        isDragging: false,
        resizeTimeout: null, // Add timeout for resize debouncing
      };
    },
    created() {
      // Make call to server, and initialise achievements
      this.initialiseAchievements();

      // Select the first achievement by default
      if (this.achievements && this.achievements.length > 0) {
        this.selectedAchievement = this.achievements[0];
        this.currentIndex = 0;
      }

      // Prepare array for scroll handling
      this.$nextTick(() => {
        this.allAchievementsForScroll = [...this.achievements];
        // Add a dummy entry for the "no more achievements" panel
        this.allAchievementsForScroll.push({
          id: 'no-more-achievements',
          title: 'No More Achievements',
          available: true,
          isEndPanel: true,
        });
        this.allAchievementsForScroll.push(...this.lockedAchievements);
      });
    },
    mounted() {
      // Add resize event listener for debounced resize handling
      window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
      // Clean up resize event listener and any pending timeouts
      window.removeEventListener('resize', this.handleResize);
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
    },
    methods: {
      // Debounced resize handler to scroll to the currently selected achievement
      handleResize() {
        // Clear any existing resize timeout
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        // Set a new timeout to debounce the resize event (300ms delay)
        this.resizeTimeout = setTimeout(() => {
          this.scrollToCurrentAchievement();
        }, 300);
      },

      // Scroll to the currently selected achievement after a resize
      scrollToCurrentAchievement() {
        if (!this.selectedAchievement || !this.$refs.achievementsList || this.isScrolling) {
          return;
        }

        const index = this.allAchievementsForScroll.findIndex(
          (a) => a.id === this.selectedAchievement.id
        );

        if (index !== -1) {
          this.currentIndex = index;

          const scrollContainer = this.$refs.achievementsList;
          const containerWidth = scrollContainer.clientWidth;

          // Scroll immediately without animation since this is a resize event
          this.isScrolling = true;
          scrollContainer.scrollTo({
            left: containerWidth * index,
            behavior: 'auto', // No animation for resize events
          });

          // Reset scrolling flag after a short delay
          setTimeout(() => {
            this.isScrolling = false;
          }, 50);
        }
      },

      nextPercentile(percentile) {
        if (percentile < 0.5) return 0.5;
        if (percentile < 0.6) return 0.6;
        if (percentile < 0.7) return 0.7;
        if (percentile < 0.8) return 0.8;
        if (percentile < 0.9) return 0.9;
        return 1;
      },
      percentileToStars(percentile) {
        if (percentile < 0.5) return 0;
        if (percentile < 0.6) return 1;
        if (percentile < 0.7) return 2;
        if (percentile < 0.8) return 3;
        if (percentile < 0.9) return 4;
        return 5;
      },
      percentileDisplay(percentile) {
        return Math.round(1000 * percentile) / 10;
      },
      sortAchievements() {
        this.achievements.sort((a, b) => {
          // First sort by stars (descending)
          if (b.stars !== a.stars) {
            return b.stars - a.stars;
          }
          // If stars are equal, sort by sortOrder (descending)
          return (b.sortOrder || 0) - (a.sortOrder || 0);
        });
        this.lockedAchievements.sort((a, b) => {
          // Streak achievements first (top-tier-tactician and daily-devotion)
          if (a.id.startsWith('top-tier-tactician') || a.id.startsWith('daily-devotion')) {
            return -1;
          }
          // Then sort by stars (ascending)
          return a.stars - b.stars;
        });
      },
      getTopSquaresPercentile(squares, bucket) {
        const totalScore = squares.reduce((acc, [, , r]) => +acc + +r, 0);
        const pivot = Math.round(totalScore / bucket._divisor);

        let total = 0;
        let worseThanUs = 0;
        for (const key in bucket) {
          if (key === '_divisor') continue;
          const value = bucket[key];
          if (+key === pivot) {
            // These people are in the same bucket as us, let's interpolate
            const p = 1 - (totalScore / bucket._divisor - pivot);
            worseThanUs += value * p;
          } else if (+key > pivot) {
            // These people did worse than us
            worseThanUs += value;
          }
          total += value;
        }
        let percentile = worseThanUs / total;
        if (percentile < 0) percentile = 0;
        if (percentile > 1) percentile = 1;
        return percentile;
      },
      formatNumber(num) {
        if (!num) return '';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
      initialiseAchievements() {
        // Check if achievements exist in localStorage
        const achievementsKey = `achievements_${this.boardId}`;
        const cachedAchievements = localStorage.getItem(achievementsKey);

        if (cachedAchievements) {
          try {
            const parsed = JSON.parse(cachedAchievements);
            if (parsed.achievements && parsed.lockedAchievements) {
              this.achievements = parsed.achievements;
              this.lockedAchievements = parsed.lockedAchievements;
              this.sortAchievements();
              return; // Exit early, we've loaded from localStorage
            }
          } catch (e) {
            console.error('Error parsing cached achievements:', e);
            // Continue with calculation if there's an error
          }
        }

        // Calculate achievements if not found in localStorage
        const legendarySquares = this.coordsAndRarities.filter(([, , r]) => r < 5);
        const topSquare = this.coordsAndRarities.slice(0, 1);
        const top2Squares = this.coordsAndRarities.slice(0, 2);
        const top3Squares = this.coordsAndRarities.slice(0, 3);

        // Calculate the top square percentiles
        const percentile = 1 - +this.rank / +this.plays;
        const suffix = this.hasMadeGuessInInfiniteMode ? '_inf' : '';
        const topSquarePercentile =
          topSquare.length < 1
            ? 0
            : this.getTopSquaresPercentile(
                topSquare,
                this.statsData.scores_by_bucket['top_1' + suffix]
              );
        const top2SquaresPercentile =
          top2Squares.length < 2
            ? 0
            : this.getTopSquaresPercentile(
                top2Squares,
                this.statsData.scores_by_bucket['top_2' + suffix]
              );
        const top3SquaresPercentile =
          top3Squares.length < 3
            ? 0
            : this.getTopSquaresPercentile(
                top3Squares,
                this.statsData.scores_by_bucket['top_3' + suffix]
              );

        // For each achievement, add it and the next available
        const achievements = [];

        if (percentile < 0.5) {
          achievements.push({
            id: 'elite-among-mortals',
            icon: '🎖️',
            title: 'Elite Among Mortals',
            description: 'Ordinary among #geogridgame savants, extraordinary among mere mortals.',
            stars: 0,
            available: true,
            keyCells: [],
          });
        }

        {
          if (percentile >= 0.5) {
            // Add the best we've achieved as available
            achievements.push({
              id: 'top-brass',
              icon: '🎺',
              title: 'Top Brass',
              description: `I scored better than ${this.percentileDisplay(percentile)}% of #geogridgame players!`,
              stars: this.percentileToStars(percentile),
              sortOrder: percentile,
              available: true,
              keyCells: [],
            });
          }
          if (percentile < 0.9) {
            // Add the next available as locked
            const nextPercentile = this.nextPercentile(percentile);
            achievements.push({
              id: 'top-brass-next',
              icon: '🎺',
              title: 'Top Brass',
              description: `Score better than ${this.percentileDisplay(nextPercentile)}% of players.`,
              descriptionLong: `Score better than ${this.percentileDisplay(nextPercentile)}% of players. Additional stars for each 10% above 50%.`,
              stars: this.percentileToStars(nextPercentile),
              available: false,
              keyCells: [],
            });
          }
        }

        {
          if (topSquarePercentile >= 0.5) {
            // Add the best we've achieved as available
            achievements.push({
              id: 'peak-performance',
              icon: '🚀',
              title: 'Peak Performance',
              description: `My best square beat ${this.percentileDisplay(topSquarePercentile)}% of #geogridgame players!`,
              stars: this.percentileToStars(topSquarePercentile),
              sortOrder: topSquarePercentile,
              available: true,
              keyCells: [...topSquare],
            });
          }
          if (topSquarePercentile < 0.9) {
            // Add the next available as locked
            const nextPercentile = this.nextPercentile(topSquarePercentile);
            achievements.push({
              id: 'peak-performance-next',
              icon: '🚀',
              title: 'Peak Performance',
              description: `Need top square to beat ${this.percentileDisplay(nextPercentile)}% of players.`,
              descriptionLong: `Your top square needs to beat ${this.percentileDisplay(nextPercentile)}% of players to unlock the next level.`,
              stars: this.percentileToStars(nextPercentile),
              available: false,
              keyCells: [],
            });
          }
        }

        {
          if (top2SquaresPercentile >= 0.5) {
            // Add the best we've achieved as available
            achievements.push({
              id: 'dynamic-duo',
              icon: ['☀️', '🌙'],
              title: 'Dynamic Duo',
              description: `My top 2 squares beat ${this.percentileDisplay(top2SquaresPercentile)}% of #geogridgame players!`,
              stars: this.percentileToStars(top2SquaresPercentile),
              sortOrder: top2SquaresPercentile,
              available: true,
              keyCells: [...top2Squares],
            });
          }
          if (top2SquaresPercentile < 0.9) {
            // Add the next available as locked
            const nextPercentile = this.nextPercentile(top2SquaresPercentile);
            achievements.push({
              id: 'dynamic-duo-next',
              icon: ['☀️', '🌙'],
              title: 'Dynamic Duo',
              description: `Top 2 squares must beat  ${this.percentileDisplay(nextPercentile)}% of players.`,
              descriptionLong: `Your top 2 squares must beat ${this.percentileDisplay(nextPercentile)}% of players to unlock the next level.`,
              stars: this.percentileToStars(nextPercentile),
              available: false,
              keyCells: [],
            });
          }
        }

        {
          if (top3SquaresPercentile >= 0.5) {
            // Add the best we've achieved as available
            achievements.push({
              id: 'triple-threat',
              icon: '🔱',
              title: 'Triple Threat',
              description: `My top 3 squares beat ${this.percentileDisplay(top3SquaresPercentile)}% of #geogridgame players!`,
              stars: this.percentileToStars(top3SquaresPercentile),
              sortOrder: top3SquaresPercentile,
              available: true,
              keyCells: [...top3Squares],
            });
          }
          if (top3SquaresPercentile < 0.9) {
            // Add the next available as locked
            const nextPercentile = this.nextPercentile(top3SquaresPercentile);
            achievements.push({
              id: 'triple-threat-next',
              icon: '🔱',
              title: 'Triple Threat',
              description: `Top 3 squares must beat  ${this.percentileDisplay(nextPercentile)}% of players.`,
              descriptionLong: `Your top 3 squares must beat ${this.percentileDisplay(nextPercentile)}% of players to unlock the next level.`,
              stars: this.percentileToStars(nextPercentile),
              available: false,
              keyCells: [],
            });
          }
        }

        {
          if (legendarySquares.length >= 2) {
            // Add the best we've achieved as available
            achievements.push({
              id: 'legendary-hunter',
              icon: '💎',
              title: 'Legendary Hunter',
              description: `I found ${legendarySquares.length} legendary squares in #geogridgame today!`,
              stars: Math.min(legendarySquares.length - 1, 5),
              available: true,
              keyCells: [...legendarySquares],
            });
          }
          if (legendarySquares.length < 6) {
            // Add the next available as locked
            achievements.push({
              id: 'legendary-hunter-next',
              icon: '💎',
              title: 'Legendary Hunter',
              description: `Find ${legendarySquares.length + 1} legendary squares in a grid.`,
              descriptionLong: `Find ${legendarySquares.length + 1} legendary squares (5% or less rarity) to unlock the next level.`,
              stars: Math.min(legendarySquares.length, 5),
              available: false,
              keyCells: [],
            });
          }
        }

        // Streak-based achievements
        if (!this.archiveMode) {
          try {
            const userDataString = localStorage.getItem('userData');
            const userData = JSON.parse(userDataString);

            // Get streak data
            let topHalfStreak = true;
            let currentStreak = 0;
            let currentStreakTopHalf = 0;
            let numberOfInfiniteModePlays = 0;
            let numberOfInfiniteModePlaysTopHalf = 0;
            while (currentStreak <= +this.boardId) {
              // Start today, and go backwards until we miss a day.
              const id = this.boardId - currentStreak;
              if (!(id in userData)) {
                break;
              }
              if (userData[id].archiveMode) {
                // Don't count archive mode
                break;
              }
              const rankForMode = userData[id].rankData?.rank_for_mode;
              const numScoresForMode = userData[id].rankData?.num_scores_for_mode;
              if (
                topHalfStreak &&
                rankForMode &&
                numScoresForMode &&
                rankForMode / numScoresForMode <= 0.5
              ) {
                currentStreakTopHalf++;
                if (userData[id].hasMadeGuessInInfiniteMode) {
                  numberOfInfiniteModePlaysTopHalf++;
                }
              } else {
                topHalfStreak = false;
              }
              if (userData[id].hasMadeGuessInInfiniteMode) {
                numberOfInfiniteModePlays++;
              }
              currentStreak++;
            }

            {
              const starBoundaries = [2, 5, 7, 15, 30];
              let numStars = 0;
              for (let i = 0; i < starBoundaries.length; i++) {
                if (currentStreakTopHalf >= starBoundaries[i]) {
                  numStars = i + 1;
                }
              }

              {
                if (currentStreakTopHalf >= 2) {
                  // Add the best we've achieved as available
                  achievements.push({
                    id: 'top-tier-tactician',
                    icon: '🧭',
                    title: 'Top Tier Tactician',
                    description: `I've scored better than 50% of #geogridgame players for ${currentStreakTopHalf} days straight!`,
                    stars: numStars,
                    available: true,
                    sortOrder: 0.5,
                    percentageInfinite: numberOfInfiniteModePlaysTopHalf / currentStreakTopHalf,
                    keyCells: [],
                  });
                }
                if (currentStreakTopHalf < 90) {
                  const next = starBoundaries[numStars];
                  // Add the next available as locked
                  achievements.push({
                    id: 'top-tier-tactician-next',
                    icon: '🧭',
                    title: 'Top Tier Tactician',
                    description: `Score better than 50% of players for ${next} days in a row in daily mode.`,
                    descriptionLong: `Score better than 50% of players for ${next} days in a row in daily mode to earn ${numStars + 1} stars. Current streak: ${currentStreakTopHalf} days.`,
                    stars: numStars + 1,
                    available: false,
                    percentageComplete: currentStreakTopHalf / next,
                    keyCells: [],
                  });
                }
              }
            }
            {
              const starBoundaries = [3, 7, 15, 30, 90];
              let numStars = 0;
              for (let i = 0; i < starBoundaries.length; i++) {
                if (currentStreak >= starBoundaries[i]) {
                  numStars = i + 1;
                }
              }

              {
                if (currentStreak >= 3) {
                  // Add the best we've achieved as available
                  achievements.push({
                    id: 'daily-devotion',
                    icon: '🔥',
                    title: 'Daily Devotion',
                    description: `I've played GeoGrid for ${currentStreak} days straight!`,
                    stars: numStars,
                    available: true,
                    percentageInfinite: numberOfInfiniteModePlays / currentStreak,
                    keyCells: [],
                  });
                }
                if (currentStreak < 90) {
                  const next = starBoundaries[numStars];
                  const missing = next - currentStreak;
                  // Add the next available as locked
                  achievements.push({
                    id: 'daily-devotion-next',
                    icon: '🔥',
                    title: 'Daily Devotion',
                    description: `Play ${missing} more days in a row in daily mode to reach ${next} days.`,
                    descriptionLong: `Play ${next} days in a row in daily mode to earn ${numStars + 1} stars. Current streak: ${currentStreak} days.`,
                    stars: numStars + 1,
                    available: false,
                    percentageComplete: currentStreak / next,
                    keyCells: [],
                  });
                }
              }
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }

        for (const a of achievements) {
          if (!a.stars || a.stars < 0) {
            a.stars = 0;
          }
          if (a.stars > 5) {
            a.stars = 5;
          }
          if (a.available) {
            this.achievements.push({ ...a });
          } else {
            this.lockedAchievements.push({ ...a });
          }
        }
        this.sortAchievements();

        // Save the calculated achievements to localStorage
        try {
          const achievementsKey = `achievements_${this.boardId}`;
          localStorage.setItem(
            achievementsKey,
            JSON.stringify({
              achievements: this.achievements,
              lockedAchievements: this.lockedAchievements,
            })
          );
        } catch (e) {
          console.error('Error saving achievements to localStorage:', e);
        }
      },
      closeNotification() {
        this.showNotification = false;
        localStorage.setItem('achievementNotificationDismissed', 'true');
      },
      selectAchievement(achievement) {
        this.selectedAchievement = achievement;
        // When an achievement is selected, automatically close the notification
        this.showNotification = false;

        // Reset the share button state for the active achievement display
        this.$nextTick(() => {
          if (this.$refs.achievementDisplay) {
            // Find the achievement display component that matches the selected achievement
            const achievementDisplays = Array.isArray(this.$refs.achievementDisplay)
              ? this.$refs.achievementDisplay
              : [this.$refs.achievementDisplay];

            for (const display of achievementDisplays) {
              if (display.achievement && display.achievement.id === achievement.id) {
                if (display.resetShareButtonState) {
                  display.resetShareButtonState();
                }
              }
            }
          }
        });

        // Scroll to the selected achievement
        this.$nextTick(() => {
          if (this.$refs.achievementsList) {
            const index = this.allAchievementsForScroll.findIndex((a) => a.id === achievement.id);
            if (index !== -1) {
              // Update current index
              this.currentIndex = index;

              const scrollContainer = this.$refs.achievementsList;
              const containerWidth = scrollContainer.clientWidth;

              // Use smooth scrolling for better user experience
              this.isScrolling = true;
              scrollContainer.scrollTo({
                left: containerWidth * index,
                behavior: 'smooth',
              });

              // Reset scrolling flag after animation completes
              setTimeout(() => {
                this.isScrolling = false;
              }, 400);
            }
          }
        });
      },
      handleScroll(e) {
        // Prevent default scrolling behavior
        if (e) e.preventDefault();
      },

      handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchCurrentX = this.touchStartX;
        this.touchCurrentScrollDir = 0;
        this.isDragging = true;
      },

      handleTouchMove(e) {
        // Store current touch position
        const nextTouchX = e.changedTouches[0].screenX;
        if (nextTouchX > this.touchCurrentX) {
          this.touchCurrentScrollDir = 1; // Scrolling right
        } else {
          this.touchCurrentScrollDir = -1; // Scrolling left
        }
        this.touchCurrentX = nextTouchX;

        // Calculate how much to scroll during the drag
        if (this.isDragging && this.$refs.achievementsList) {
          const scrollContainer = this.$refs.achievementsList;
          const containerWidth = scrollContainer.clientWidth;

          // Calculate the drag distance
          const dragDistance = this.touchCurrentX - this.touchStartX;

          // Calculate the base position (current index * container width)
          const basePosition = this.currentIndex * containerWidth;

          // Apply drag offset (negative because we need to move in the opposite direction)
          // Use a resistance factor to make the movement feel more controlled
          const resistanceFactor = 2;
          const newPosition = basePosition - dragDistance / resistanceFactor;

          // Apply the scroll position directly
          scrollContainer.scrollLeft = newPosition;
        }

        // Prevent default to disable browser's default scrolling behavior
        e.preventDefault();
      },

      handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.isDragging = false;

        // Prevent further processing if no achievements list or already scrolling
        if (!this.$refs.achievementsList || this.isScrolling) return;

        const scrollContainer = this.$refs.achievementsList;
        const totalItems = this.allAchievementsForScroll.length;

        // Check the swipe direction
        const swipeDistance = this.touchEndX - this.touchStartX;
        const containerWidth = scrollContainer.clientWidth;
        let targetIndex = this.currentIndex;

        // If swipe distance is very small (less than 5px), snap back to current achievement
        if (
          Math.abs(swipeDistance) < 10 ||
          this.touchCurrentScrollDir === 0 ||
          (this.touchCurrentScrollDir < 0 && swipeDistance > 0) ||
          (this.touchCurrentScrollDir > 0 && swipeDistance < 0)
        ) {
          // We should stay at current index
        } else {
          if (this.touchCurrentScrollDir < 0) {
            // Swipe left (next achievement)
            targetIndex = Math.min(this.currentIndex + 1, totalItems - 1);
          } else {
            // Swipe right (previous achievement)
            targetIndex = Math.max(this.currentIndex - 1, 0);
          }
        }

        // Set scrolling flag to prevent other scroll operations
        this.isScrolling = true;
        const targetPosition = targetIndex * containerWidth;

        // Perform the scroll
        scrollContainer.scrollTo({
          left: targetPosition,
          behavior: 'smooth',
        });

        // Update current index and selected achievement
        if (targetIndex != this.currentIndex) {
          this.currentIndex = targetIndex;
          const newSelectedAchievement = this.allAchievementsForScroll[targetIndex];
          // Don't select the "no more achievements" panel as an achievement
          if (newSelectedAchievement && this.selectedAchievement.id !== newSelectedAchievement.id) {
            this.selectedAchievement = newSelectedAchievement;
          }
        }

        // Reset scrolling flag after animation completes
        setTimeout(() => {
          this.isScrolling = false;
        }, 300); // A bit longer than the scroll animation
      },

      navigateToAchievement(targetIndex) {
        if (this.isScrolling || !this.$refs.achievementsList) return;

        const totalItems = this.allAchievementsForScroll.length;

        // Ensure target index is within bounds
        if (targetIndex < 0 || targetIndex >= totalItems) return;

        const scrollContainer = this.$refs.achievementsList;
        const containerWidth = scrollContainer.clientWidth;

        // Set scrolling flag to prevent other scroll operations
        this.isScrolling = true;
        const targetPosition = targetIndex * containerWidth;

        // Perform the scroll
        scrollContainer.scrollTo({
          left: targetPosition,
          behavior: 'smooth',
        });

        // Update current index and selected achievement
        this.currentIndex = targetIndex;
        const newSelectedAchievement = this.allAchievementsForScroll[targetIndex];
        // Don't select the "no more achievements" panel as an achievement
        if (newSelectedAchievement && this.selectedAchievement.id !== newSelectedAchievement.id) {
          this.selectedAchievement = newSelectedAchievement;
        }

        // Reset scrolling flag after animation completes
        setTimeout(() => {
          this.isScrolling = false;
        }, 300);
      },
    },
  };
</script>

<style scoped>
  .achievements-content {
    position: relative;
    height: 100%;
    overflow-y: auto;
  }

  @keyframes pulse {
    0% {
      opacity: 0.8;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.8;
    }
  }

  .notification-banner {
    background-color: rgba(147, 147, 236, 1);
    color: black;
    padding: 12px 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .pulse-animation {
    animation: pulse 2s infinite ease-in-out;
  }

  .close-btn {
    background: none;
    border: none;
    color: black;
    font-size: 16px;
    cursor: pointer;
  }

  .achievement-display {
    border-bottom: 1px solid #374051;
    background-color: rgba(76, 76, 103, 1);
    padding: 20px;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .achievement-display-content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .achievements-list-container {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .chevron-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    background: rgba(0, 0, 0, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(2px);
  }

  .chevron-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .chevron-btn:disabled:hover {
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.2);
  }

  .chevron-left {
    left: 0;
  }

  .chevron-right {
    right: 0;
  }

  .achievements-list {
    display: flex;
    flex-direction: row;
    overflow-x: hidden; /* Change from auto to hidden to prevent manual scrolling */
    width: 100%;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    touch-action: none; /* Disable browser's default touch actions */
    user-select: none; /* Prevent text selection during drag */
  }

  .achievements-list::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  .achievements-list > div {
    flex: 0 0 100%;
    width: 100%;
    min-width: 100%;
    padding: 0 50px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .no-more-achievements-panel {
    flex: 0 0 100%;
    width: 100%;
    min-width: 100%;
    padding: 0 50px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #cccccc;
    text-align: center;
  }

  .no-more-icon {
    background-color: rgba(147, 147, 236, 1);
    color: #000;
    border-radius: 100%;
    width: 50px;
    height: 50px;
    margin: 0 auto 20px;
    padding: 10px;
  }

  .no-more-title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .no-more-description {
    margin-bottom: 20px;
  }

  .no-more-mini-description {
    font-size: small;
    color: #8d8f97;
    margin-bottom: 10px;
  }

  .no-more-mini-description.no-more-last {
    margin-bottom: 40px;
  }

  .mobile-only {
    display: none;
  }

  .game-summary-notification {
    background-color: #202937;
    border: 1px solid #374051;
    padding: 10px;
    border-radius: 5px;
    margin: 10px;
  }

  .mobile-only.game-summary-notification {
    font-size: small;
  }

  .inline-svg {
    display: inline-block;
    vertical-align: middle;
  }

  .mobile-only.inline-svg {
    display: none;
  }

  .button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 10px auto;
    padding: 10px;
    background-color: rgba(147, 147, 236, 1);
    color: black;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    font-size: 1rem;
    cursor: pointer;
    width: 200px;
  }

  .button svg {
    margin-right: 10px;
  }

  .button-icon {
    flex-shrink: 0;
  }

  .no-more-buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .no-more-buttons button {
    margin: 5px 0;
  }

  @media (max-width: 768px), (max-height: 980px) {
    .no-more-buttons button {
      font-size: small;
      padding: 8px;
    }

    .mobile-only {
      display: block;
    }
    .mobile-only.inline-svg {
      display: inline-block !important;
    }
    .desktop-only {
      display: none;
    }

    .achievements-content {
      display: flex;
      flex-direction: column;
    }

    .achievement-display {
      overflow-y: hidden;
      position: static;
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center safe;
      padding: 5px;
    }

    .no-more-achievements-panel {
      padding: 0 20px;
    }

    .no-more-icon {
      font-size: 36px;
      margin-bottom: 15px;
    }

    .no-more-title {
      font-size: 20px;
      margin-bottom: 8px;
    }

    .no-more-description {
      font-size: 14px;
    }

    .no-more-mini-description.no-more-last {
      margin-bottom: 10px;
    }
  }

  @media (max-height: 650px) {
    .no-more-last,
    .no-more-mini-description {
      display: none;
    }
    .no-more-description {
      margin-bottom: 0;
    }
  }
</style>
